import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useMotionValue, useSpring, useTransform, useScroll, useMotionTemplate } from "framer-motion";
import { ArrowRight, Phone } from "lucide-react";
import { MaskLines } from "@/components/site/Reveal";
import { SITE } from "@/lib/site";

const RotatingBadge = () => (
  <div className="absolute -left-10 top-16 z-20 h-32 w-32 sm:h-36 sm:w-36 hidden sm:block" aria-hidden="true">
    <svg viewBox="0 0 120 120" className="absolute inset-0 animate-spin-slow">
      <defs><path id="circ" d="M60,60 m-46,0 a46,46 0 1,1 92,0 a46,46 0 1,1 -92,0" /></defs>
      <text className="fill-white font-mono" fontSize="10.5" letterSpacing="3.4"><textPath href="#circ">SYDNEY · 30 YEARS · DOMESTIC · COMMERCIAL · </textPath></text>
    </svg>
    <div className="absolute inset-[30%] rounded-full bg-brass flex items-center justify-center font-wide font-black text-ink text-xl">30</div>
  </div>
);

const TiltFrame = () => {
  const ref = useRef(null);
  const mx = useMotionValue(0.5), my = useMotionValue(0.5);
  const rx = useSpring(useTransform(my, [0, 1], [7, -7]), { stiffness: 120, damping: 18 });
  const ry = useSpring(useTransform(mx, [0, 1], [-9, 9]), { stiffness: 120, damping: 18 });
  const spotX = useTransform(mx, [0, 1], ["0%", "100%"]), spotY = useTransform(my, [0, 1], ["0%", "100%"]);
  const spot = useMotionTemplate`radial-gradient(420px circle at ${spotX} ${spotY}, rgba(91,143,230,0.32), transparent 60%)`;
  const onMove = (e) => {
    const r = ref.current.getBoundingClientRect();
    mx.set((e.clientX - r.left) / r.width); my.set((e.clientY - r.top) / r.height);
  };
  const reset = () => { mx.set(0.5); my.set(0.5); };
  return (
    <div className="[perspective:1400px]" onMouseMove={onMove} onMouseLeave={reset} ref={ref}>
      <motion.div style={{ rotateX: rx, rotateY: ry, transformStyle: "preserve-3d" }} className="relative"
        initial={{ opacity: 0, scale: 0.94 }} animate={{ opacity: 1, scale: 1 }} transition={{ duration: 1.2, delay: 0.4, ease: [0.22, 1, 0.36, 1] }}>
        <div className="relative aspect-[4/5] sm:aspect-[5/6] clip-notch overflow-hidden bg-ink2">
          <motion.img initial={{ scale: 1.25 }} animate={{ scale: 1.05 }} transition={{ duration: 2.2, ease: [0.22, 1, 0.36, 1] }}
            src="/images/ig/p10_14.jpg" alt="Freestanding bath and brass tapware fitted by Austyle Plumbing in Cronulla" className="absolute inset-0 h-full w-full object-cover" data-testid="hero-image" />
          <div className="absolute inset-0 bg-gradient-to-t from-ink via-ink/10 to-transparent" />
          <motion.div className="absolute inset-0 mix-blend-screen" style={{ background: spot }} />
          <div className="absolute bottom-0 inset-x-0 p-6 flex items-end justify-between" style={{ transform: "translateZ(40px)" }}>
            <div>
              <p className="eyebrow text-brass">Recent work · Cronulla</p>
              <p className="font-wide font-extrabold text-white text-lg mt-1">Bathrooms · Kitchens · New builds</p>
            </div>
          </div>
        </div>
        <div className="absolute -right-3 -top-3 h-24 w-24 border-r-2 border-t-2 border-brass" style={{ transform: "translateZ(60px)" }} />
        <RotatingBadge />
      </motion.div>
    </div>
  );
};

export const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yText = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yArt = useTransform(scrollYProgress, [0, 1], [0, -60]);
  return (
    <section ref={ref} data-testid="home-hero" className="relative min-h-[100svh] bg-ink text-white overflow-hidden grain">
      <div className="absolute inset-0 blueprint" />
      <div className="absolute -right-40 top-1/3 h-[38rem] w-[38rem] rounded-full bg-cobalt/30 blur-[140px]" />
      <div className="container-x relative pt-32 sm:pt-40 pb-16 grid gap-14 lg:grid-cols-12 items-center">
        <motion.div style={{ y: yText }} className="lg:col-span-7">
          <motion.p initial={{ opacity: 0, y: 12 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.7 }}
            className="eyebrow text-brass flex items-center gap-3 mb-8">
            <span className="h-px w-10 bg-brass" /> Sydney plumbers · {SITE.years} years
          </motion.p>
          <h1 data-testid="hero-heading" className="font-wide font-black uppercase leading-[0.9] tracking-[-0.02em] text-[2.6rem] sm:text-6xl lg:text-[5.4rem] xl:text-[6.2rem]">
            <MaskLines animateOnLoad delay={0.2} lines={["Sydney's", <span key="p" className="text-brass">plumbing,</span>, "done properly."]} />
          </h1>
          <motion.p initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-8 max-w-xl text-slate-300 text-base md:text-lg leading-relaxed">
            New builds, strata, maintenance and everything in between. Three decades of domestic, commercial and industrial plumbing — from a tap washer to a full development.
          </motion.p>
          <motion.div initial={{ opacity: 0, y: 16 }} animate={{ opacity: 1, y: 0 }} transition={{ duration: 0.8, delay: 1 }} className="mt-10 flex flex-col sm:flex-row gap-3">
            <Link to="/book" data-testid="hero-book-button" className="group inline-flex items-center justify-center gap-3 bg-brass text-ink font-bold h-14 px-8 rounded-full hover:bg-white transition-colors">
              Book a plumber <ArrowRight className="h-5 w-5 transition-transform group-hover:translate-x-1" />
            </Link>
            <a href={SITE.tel} data-testid="hero-call-button" className="inline-flex items-center justify-center gap-3 border border-white/25 h-14 px-8 rounded-full font-mono hover:border-brass hover:text-brass transition-colors">
              <Phone className="h-4 w-4" /> {SITE.phone}
            </a>
          </motion.div>
        </motion.div>
        <motion.div style={{ y: yArt }} className="lg:col-span-5 max-w-md lg:max-w-none w-full mx-auto">
          <TiltFrame />
        </motion.div>
      </div>
      <div className="container-x relative pb-10">
        <div className="grid grid-cols-2 md:grid-cols-4 border-t border-white/15">
          {[["30", "Years in the trade"], ["3", "Sectors: domestic, commercial, industrial"], ["9+", "Specialist services"], ["SYD", "Sydney-wide service"]].map(([n, l], i) => (
            <motion.div key={l} initial={{ opacity: 0 }} animate={{ opacity: 1 }} transition={{ delay: 1.1 + i * 0.1 }} className="pt-6 pr-4 pb-2">
              <p className="font-wide font-black text-3xl sm:text-4xl text-white">{n}</p>
              <p className="text-xs sm:text-sm text-slate-400 mt-1">{l}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
